import { Box } from "@mui/material";
import { captureException } from "@sentry/react";
import {
  useMutation,
  useQueryClient,
  useQueryErrorResetBoundary,
} from "@tanstack/react-query";
import {
  createFileRoute,
  ErrorComponent,
  type ErrorComponentProps,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { z } from "zod";
import type {
  InfiniteNotificationsData,
  Notification,
} from "../../@types/services/Notification";
import { AnnouncementBanner } from "../../components/organisms/AnnouncementBanner";
import { AnnouncementModal } from "../../components/organisms/AnnouncementModal";
import { LeftMenu } from "../../components/organisms/LeftMenu";
import { TopMenu } from "../../components/organisms/TopMenu";
import { Page403 } from "../../components/templates/Page403";
import { Page404 } from "../../components/templates/Page404";
import { Page500 } from "../../components/templates/Page500";
import { AppError } from "../../errors/AppError";
import { useAuth } from "../../hooks/useAuth";
import { useChatRealtime } from "../../hooks/useChatRealtime";
import { useLeftMenu } from "../../hooks/useLeftMenu";
import { useTopMenu } from "../../hooks/useTopMenu";
import {
  markAsReadAndSeenNotification,
  markAsSeenNotification,
} from "../../services/notifications";

const RootComponent: React.FC = () => {
  const {
    chat_notification_id: chatNotificationId,
    notification_id: notificationId,
  } = Route.useSearch();

  const navigate = Route.useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  useChatRealtime(user?.id);
  const queryClient = useQueryClient();
  const { topMenuWidth } = useTopMenu();
  const { leftMenuWidth } = useLeftMenu();

  const [leftMenuOpen, setLeftMenuOpen] = useState(true);

  const { mutate: markAsReadAndSeenMutation } = useMutation({
    mutationFn: markAsReadAndSeenNotification,
    onError: (error) => captureException(error),
    onSuccess: (updatedNotification) => {
      queryClient.setQueryData(
        ["notifications", "list", user?.id || ""],
        (oldData?: InfiniteNotificationsData) => {
          if (!oldData?.pages) return oldData;

          // Filter out the read notification and update total_items
          const updatedPages = oldData.pages.map((page) => ({
            ...page,
            results:
              page.results?.filter(
                (notification: Notification) =>
                  notification.id !== updatedNotification.id,
              ) || [],
            total_items: page.total_items - 1,
          }));

          // Remove empty pages, except keep at least one page
          const nonEmptyPages = updatedPages.filter(
            (page) => page.results && page.results.length > 0,
          );
          const finalPages =
            nonEmptyPages.length > 0 ? nonEmptyPages : [updatedPages[0]];

          return {
            ...oldData,
            pageParams: finalPages.map((_, index) => index + 1),
            pages: finalPages,
          };
        },
      );
    },
  });

  const { mutate: markChatNotificationSeen } = useMutation({
    mutationFn: markAsSeenNotification,
    onError: (error) => captureException(error),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    // Google Analytics
    // ga('send', 'pageview');
    if (notificationId) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          notification_id: undefined,
        }),
        to: location.pathname,
      });
      markAsReadAndSeenMutation(notificationId);
    }
  }, [location.pathname, markAsReadAndSeenMutation, navigate, notificationId]);

  useEffect(() => {
    if (chatNotificationId) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          chat_notification_id: undefined,
        }),
        to: location.pathname,
      });
      markChatNotificationSeen(chatNotificationId);
    }
  }, [
    chatNotificationId,
    location.pathname,
    markChatNotificationSeen,
    navigate,
  ]);

  return (
    <>
      <TopMenu setLeftMenuOpen={setLeftMenuOpen} />
      <Box sx={{ display: "flex" }}>
        <LeftMenu
          leftMenuOpen={leftMenuOpen}
          setLeftMenuOpen={setLeftMenuOpen}
        />
        <Box
          sx={{
            flexDirection: "column",
            flexGrow: 1,
            width: `calc(${topMenuWidth}px - ${leftMenuWidth}px)`,
          }}
        >
          <AnnouncementModal />
          <AnnouncementBanner />
          <Outlet />
        </Box>
      </Box>
    </>
  );
};

const ErrorBoundaryComponent: React.FC<ErrorComponentProps> = ({ error }) => {
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    // Reset the query error boundary
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  if (error instanceof AxiosError) {
    if (error.response?.status === 404) {
      return <Page404 />;
    }
    if (error.response?.status === 403) {
      return <Page403 />;
    }

    return <Page500 />;
  }

  if (error instanceof AppError) {
    if (error.status === 403) {
      return <Page403 />;
    }

    return <Page500 />;
  }

  // Fallback to the default ErrorComponent
  return <ErrorComponent error={error} />;
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private")({
  validateSearch: z.object({
    chat_notification_id: z.optional(z.string()).catch(""),
    notification_id: z.optional(z.string()).catch(""),
  }),
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        search: {
          redirect_uri: location.href,
        },
        to: "/users/sign_in",
      });
    }

    if (context.auth.user?.vendor?.pending_acceptance_of_mandatory_terms) {
      throw redirect({
        to: "/onboarding/vendor/terms",
      });
    }
  },
  component: RootComponent,
  errorComponent: ErrorBoundaryComponent,
});
